import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";
import LimeLogo from "../../assets/lime-logo-no-bg.png";
import DeployLegalFooter from "../../components/deploy/DeployLegalFooter";

const SECTIONS = [
  { id: "terms",    label: "Terms of Service" },
  { id: "use",      label: "Acceptable Use" },
  { id: "billing",  label: "Billing" },
  { id: "privacy",  label: "Privacy & GDPR" },
  { id: "sla",      label: "Uptime & SLA" },
  { id: "ip",       label: "Intellectual Property" },
];

export default function LimeliteDeployLegalPage() {
  const scroll = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <SEO
        title="Limelite Deploy — Legal"
        description="Terms of service, acceptable use, billing, privacy, GDPR, and SLA for Limelite Deploy."
        url="/limelite/deploy/legal"
      />
      <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)] px-4 py-12">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="flex items-center gap-3 mb-10">
            <img src={LimeLogo} alt="Limelite" className="w-7 h-7" />
            <div>
              <p className="text-xs uppercase tracking-widest text-lime-500">Limelite Deploy</p>
              <h1 className="text-3xl font-serif font-medium">Legal</h1>
            </div>
          </div>

          <p className="text-silver-medium text-sm mb-2">
            This page covers the key legal terms for the Limelite Deploy service. These summaries are complemented by the detailed{" "}
            <Link to="/limelite/deploy/terms" className="text-lime-600 hover:text-lime-400 transition-colors">Terms of Service page</Link>{" "}
            and the{" "}
            <Link to="/limelite/deploy/limitations" className="text-lime-600 hover:text-lime-400 transition-colors">Limitations page</Link>.
          </p>
          <p className="text-silver-dark text-xs mb-10">
            Last updated: June 2026. Riviera Tech Solutions — registered in the EU.
          </p>

          {/* Jump links */}
          <nav className="flex flex-wrap gap-2 mb-12">
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => scroll(s.id)}
                className="text-xs border border-gray-800 hover:border-gray-600 text-silver-dark hover:text-silver-light rounded px-3 py-1.5 transition-colors"
              >
                {s.label}
              </button>
            ))}
          </nav>

          <div className="space-y-14">

            {/* Terms of Service */}
            <section id="terms" className="scroll-mt-20 space-y-4 text-sm text-silver-medium">
              <h2 className="text-xl text-silver-light font-serif font-medium">Terms of Service</h2>
              <p>
                By subscribing to Limelite Deploy you agree to these terms. Riviera Tech Solutions
                ("we", "us") provides a hosted MCP control plane and automation to deploy your code to AWS
                (default region <span className="text-silver-light">eu-north-1</span>).
              </p>
              <p>
                The service is provided on a subscription basis billed in EUR via Stripe. You get access
                to the MCP server, deploy tooling, dashboards, and all provisioned infrastructure under your
                account until you cancel or we terminate for cause.
              </p>
              <p>
                We may update these terms with 30 days' notice. Continued use of the service after that
                period constitutes acceptance. For authoritative legal text see{" "}
                <Link to="/limelite/deploy/terms" className="text-lime-600 hover:text-lime-400 transition-colors">
                  the full Terms of Service page →
                </Link>
              </p>
            </section>

            {/* Acceptable Use */}
            <section id="use" className="scroll-mt-20 space-y-4 text-sm text-silver-medium">
              <h2 className="text-xl text-silver-light font-serif font-medium">Acceptable Use Policy</h2>
              <p>You must not use Limelite Deploy to:</p>
              <ul className="space-y-2 ml-4 list-disc list-inside marker:text-lime-700">
                <li>Deploy applications that are illegal or violate third-party rights.</li>
                <li>Run crypto mining, botnet infrastructure, spam, or phishing workloads.</li>
                <li>Attack, probe, or scrape third-party systems without authorization.</li>
                <li>Circumvent platform safety controls, rate limits, or spend caps.</li>
                <li>Redistribute or resell the MCP service without written consent.</li>
              </ul>
              <p>
                You remain responsible for your code, dependencies, secrets hygiene, licensing, and the
                outbound traffic your workloads generate. We provision infrastructure on your behalf — we
                do not audit your application code.
              </p>
              <p>
                Automated abuse detection (anomaly detection, spend caps, resource caps, threat flags) runs
                continuously. Accounts flagged for violations may be suspended without prior notice. See the{" "}
                <Link to="/limelite/deploy/limitations" className="text-lime-600 hover:text-lime-400 transition-colors">
                  Limitations page →
                </Link>{" "}
                for platform-enforced constraints.
              </p>
            </section>

            {/* Billing */}
            <section id="billing" className="scroll-mt-20 space-y-4 text-sm text-silver-medium">
              <h2 className="text-xl text-silver-light font-serif font-medium">Billing</h2>

              <div className="bg-off-black border border-gray-800 rounded-xl p-5 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-silver-dark">Base subscription</span>
                  <span className="text-silver-light font-medium">€29 / month</span>
                </div>
                <div className="flex justify-between text-sm border-t border-gray-800 pt-3">
                  <span className="text-silver-dark">AWS usage (metered)</span>
                  <span className="text-silver-light font-medium">at cost + margin</span>
                </div>
                <div className="flex justify-between text-sm border-t border-gray-800 pt-3">
                  <span className="text-silver-dark">Currency</span>
                  <span className="text-silver-light font-medium">EUR, via Stripe</span>
                </div>
                <div className="flex justify-between text-sm border-t border-gray-800 pt-3">
                  <span className="text-silver-dark">Billing cycle</span>
                  <span className="text-silver-light font-medium">Monthly, in advance</span>
                </div>
              </div>

              <p>
                Subscriptions auto-renew monthly. You may cancel at any time from your account page; access
                continues until the end of the paid period. No refunds are issued for partial months.
              </p>
              <p>
                AWS resource costs (compute, storage, bandwidth, IP addresses) are metered and billed in
                arrears on top of the base subscription. We display a running spend estimate in your dashboard.
                A platform-wide monthly spend cap (default €100) blocks new deployments if exceeded; existing
                workloads continue running.
              </p>
              <p>
                Invoices are available in EUR from your{" "}
                <Link to="/limelite/account" className="text-lime-600 hover:text-lime-400 transition-colors">account page</Link>{" "}
                and the Stripe billing portal.
              </p>
              <p>
                Disputed charges must be raised within 30 days of the invoice date. Contact{" "}
                <a href="mailto:support@rivieratech.solutions" className="text-lime-600 hover:text-lime-400 transition-colors">
                  support@rivieratech.solutions
                </a>.
              </p>
            </section>

            {/* Privacy & GDPR */}
            <section id="privacy" className="scroll-mt-20 space-y-4 text-sm text-silver-medium">
              <h2 className="text-xl text-silver-light font-serif font-medium">Privacy & GDPR</h2>
              <p>
                Riviera Tech Solutions is the data controller for account and billing data. Infrastructure
                (EC2, RDS, S3) provisioned for you is operated as a data processor on your behalf — you
                are responsible for the personal data your application collects.
              </p>
              <p>
                We collect and store: your email address, billing details via Stripe (we do not store card
                numbers), deploy logs, and infrastructure metadata. We do not sell personal data to third
                parties.
              </p>
              <p>
                Under GDPR you have the right to access, correct, export, and delete your data. You can
                exercise all three rights from your{" "}
                <Link to="/limelite/account" className="text-lime-600 hover:text-lime-400 transition-colors">account settings</Link>
                : export as JSON or ZIP, or submit a deletion request. Account deletion schedules all data
                for removal within 30 days and cancels your subscription.
              </p>
              <p>
                Infrastructure defaults to <span className="text-silver-light">eu-north-1 (Stockholm)</span> for
                EU data residency. Stripe (payment processor) and MongoDB Atlas (database) have their own
                privacy policies and data processing agreements.
              </p>
              <p>
                For DPA requests or privacy concerns contact{" "}
                <a href="mailto:privacy@rivieratech.solutions" className="text-lime-600 hover:text-lime-400 transition-colors">
                  privacy@rivieratech.solutions
                </a>.
              </p>
            </section>

            {/* Uptime & SLA */}
            <section id="sla" className="scroll-mt-20 space-y-4 text-sm text-silver-medium">
              <h2 className="text-xl text-silver-light font-serif font-medium">Uptime & SLA</h2>
              <p>
                We target 99% uptime for the MCP control plane (deploy API, dashboards). Planned maintenance
                windows are announced at least 24 hours in advance.
              </p>
              <p>
                Your deployed applications run on your own EC2 instances. Their uptime depends on the
                application code, the instance type chosen, and AWS regional availability. We provide
                health checks, automated restart on crash, and diagnostic tooling — but we do not guarantee
                application-level uptime.
              </p>
              <p>
                The service is provided "as is" and "as available". We are not liable for losses caused by
                outages, bugs, data loss, or third-party service failures (AWS, Stripe, Atlas). Maximum
                liability is limited to fees paid in the 3 months preceding the claim.
              </p>
            </section>

            {/* Intellectual Property */}
            <section id="ip" className="scroll-mt-20 space-y-4 text-sm text-silver-medium">
              <h2 className="text-xl text-silver-light font-serif font-medium">Intellectual Property</h2>
              <p>
                You retain all rights to your application code, content, and data. By using the service you
                grant us a limited licence to host, execute, and transmit your code as necessary to provide
                the service.
              </p>
              <p>
                The Limelite and Riviera Tech Solutions brand, logos, platform code, and documentation are
                our intellectual property. You may not reproduce or redistribute them without written consent.
              </p>
              <p>
                Open-source components used in the platform are licenced under their respective terms
                (MIT, Apache 2.0, and similar permissive licences for server-side dependencies).
              </p>
            </section>

          </div>

          {/* Nav links */}
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-wrap gap-4 text-sm text-silver-dark">
            <Link to="/limelite/deploy" className="hover:text-silver-light transition-colors">← Limelite Deploy</Link>
            <Link to="/limelite/deploy/terms" className="hover:text-silver-light transition-colors">Full Terms of Service</Link>
            <Link to="/limelite/deploy/limitations" className="hover:text-silver-light transition-colors">Platform limitations</Link>
            <Link to="/limelite/account" className="hover:text-silver-light transition-colors">Account settings</Link>
          </div>

          <div className="mt-10">
            <DeployLegalFooter />
          </div>
        </div>
      </div>
    </>
  );
}
